// The validation results on the Train page (screens/train/ValidationResults.tsx): the score over the run, the best
// epoch's score per class, and what to do about it, in plain words. Web only for now (docs/DESKTOP_PORT_BACKLOG.md).
import type { TrainingMetrics } from "../api/ml";

export type Band = "good" | "usable" | "weak";
export interface ValPoint { epoch: number; map50: number; map50_95: number | null }
export interface ClassResult { name: string; ap50: number; band: Band }
export interface Results {
  points: ValPoint[]; best: ValPoint; totalEpochs: number;
  classes: ClassResult[]; band: Band; verdict: string; advice: string[];
}

/** mAP50 / AP50 bands, as the guide on the page explains them. */
export const band = (v: number): Band => (v >= 0.8 ? "good" : v >= 0.5 ? "usable" : "weak");
export const BAND_LABEL: Record<Band, string> = { good: "Good", usable: "Usable", weak: "Needs work" };

const VERDICT: Record<Band, string> = {
  good: "Good: the model finds most objects in images it has not seen.",
  usable: "Usable: the model finds many objects, but more (and more varied) images will improve it.",
  weak: "Needs work: the model misses or misplaces many objects in images it has not seen.",
};

/** Results for a run, or null until it has been validated at least once. */
export function summarizeResults(m: TrainingMetrics | null): Results | null {
  const points: ValPoint[] = (m?.epochs ?? [])
    .filter((e) => e.map50 !== null)
    .map((e) => ({ epoch: e.epoch, map50: e.map50!, map50_95: e.map50_95 }));
  if (!m || !points.length) return null;
  const best = points.reduce((a, b) => (b.map50 > a.map50 ? b : a));
  const last = points[points.length - 1];
  const totalEpochs = m.epochs[m.epochs.length - 1].total_epochs || last.epoch;
  const perClass = m.epochs.find((e) => e.epoch === best.epoch)?.per_class_ap50 ?? {};
  const classes = Object.entries(perClass).map(([name, ap50]) => ({ name, ap50, band: band(ap50) })).sort((a, b) => a.ap50 - b.ap50);

  const advice: string[] = [];
  const weak = classes.filter((c) => c.band === "weak");
  if (weak.length && weak.length < classes.length) {
    advice.push(`Weakest: ${weak.slice(0, 3).map((c) => `${c.name} (${c.ap50.toFixed(2)})`).join(", ")}. Annotate more images showing ${weak.length === 1 ? "this class" : "these classes"}, and check that the existing boxes are drawn consistently.`);
  }
  const finished = m.status === "completed" || last.epoch >= totalEpochs;
  if (finished && best.epoch === last.epoch && points.length >= 3 && last.map50 - points[points.length - 2].map50 > 0.01) {
    advice.push("The score was still rising at the end: training again with more epochs may help.");
  } else if (best.epoch < last.epoch && best.map50 - last.map50 > 0.05) {
    advice.push(`The score peaked at epoch ${best.epoch} and fell after it. The saved model (best.pt) is from epoch ${best.epoch}; next time, fewer epochs would do.`);
  }
  const b = band(best.map50);
  return { points, best, totalEpochs, classes, band: b, verdict: VERDICT[b], advice };
}
